// frontend/src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewWithCutline, setPreviewWithCutline] = useState(null);

  // ✅ 화이트 미리보기
  const [previewWhite, setPreviewWhite] = useState(null);

  // ✅ 조판 옵션 + 조판 미리보기
  const [useSheet, setUseSheet] = useState(false);
  const [sheetWmm, setSheetWmm] = useState(900);
  const [sheetHmm, setSheetHmm] = useState(600);
  const [safeAbsMm, setSafeAbsMm] = useState(2);
  const [safePct, setSafePct] = useState(5);
  const [qty, setQty] = useState(30);
  const [sheetPreview, setSheetPreview] = useState(null);
  const [sheetWhitePreview, setSheetWhitePreview] = useState(null); // 추가: 화이트 조판
  const [sheetOriginalPreview, setSheetOriginalPreview] = useState(null); // 추가: 원본 조판


  const [mode, setMode] = useState('basic');
  const [offsetMm, setOffsetMm] = useState(3.0);
  const [totalSizeMm, setTotalSizeMm] = useState(100.0);
  const [holeDiameterMm, setHoleDiameterMm] = useState(5.0);
  const [holeX, setHoleX] = useState('');
  const [holeY, setHoleY] = useState('');
  const [standHeightMm, setStandHeightMm] = useState(45.0);
  const [tabScale, setTabScale] = useState(1.0);
  const [splitFiles, setSplitFiles] = useState(false);
  const [format, setFormat] = useState('svg');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [sizeInfo, setSizeInfo] = useState(null);

  // ✅ 메모리 누수 방지: 이전 ObjectURL 해제
  const safeSetObjectUrl = (setter, nextBlob) => {
    setter((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(nextBlob);
    });
  };

  // 크기 계산
  const calculateSize = useCallback(async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('total_size_mm', totalSizeMm);

    try {
      const response = await axios.post('/api/calculate-size', formData);
      setSizeInfo(response.data);
    } catch (error) {
      console.error('크기 계산 오류:', error);
    }
  }, [selectedFile, totalSizeMm]);

  // 칼선 포함 미리보기
  const updatePreview = useCallback(async () => {
    if (!selectedFile) return;

    setMessage('미리보기 생성 중...');

    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('mode', mode);
    formData.append('offset_mm', offsetMm);
    formData.append('total_size_mm', totalSizeMm);
    formData.append('hole_diameter_mm', holeDiameterMm);
    formData.append('stand_height_mm', standHeightMm);
    formData.append('tab_scale', tabScale);

    if (holeX) formData.append('hole_x', holeX);
    if (holeY) formData.append('hole_y', holeY);

    try {
      const response = await axios.post('/api/preview', formData, {
        responseType: 'blob'
      });

      safeSetObjectUrl(setPreviewWithCutline, response.data);
      setMessage('미리보기 생성 완료');
    } catch (error) {
      console.error('미리보기 생성 오류:', error);
      setMessage('미리보기 생성 중 오류가 발생했습니다.');
    }
  }, [
    selectedFile, mode, offsetMm, totalSizeMm, holeDiameterMm,
    standHeightMm, tabScale, holeX, holeY
  ]);

  // ✅ 화이트 미리보기(파일 선택 시 1회)
  const updateWhitePreview = useCallback(async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const response = await axios.post('/api/preview-white', formData, {
        responseType: 'blob'
      });
      safeSetObjectUrl(setPreviewWhite, response.data);
    } catch (error) {
      console.error('화이트 미리보기 생성 오류:', error);
    }
  }, [selectedFile]);

    // ✅ 조판 미리보기(옵션 켰을 때만) - 칼선/화이트/원본 3종
  const updateSheetPreview = useCallback(async () => {
    if (!selectedFile || !useSheet) return;

    const makeFormData = () => {
      const fd = new FormData();
      fd.append('image', selectedFile);

      // 칼선 파라미터
      fd.append('mode', mode);
      fd.append('offset_mm', offsetMm);
      fd.append('total_size_mm', totalSizeMm);
      fd.append('hole_diameter_mm', holeDiameterMm);
      fd.append('stand_height_mm', standHeightMm);
      fd.append('tab_scale', tabScale);

      if (holeX) fd.append('hole_x', holeX);
      if (holeY) fd.append('hole_y', holeY);

      // 조판 옵션
      fd.append('sheet_w_mm', sheetWmm);
      fd.append('sheet_h_mm', sheetHmm);
      fd.append('safe_abs_mm', safeAbsMm);
      fd.append('safe_pct', safePct);
      fd.append('qty', qty);

      return fd;
    };

    try {
      const [cutRes, whiteRes, origRes] = await Promise.all([
        axios.post('/api/sheet-preview', makeFormData(), { responseType: 'blob' }),
        axios.post('/api/sheet-preview-white', makeFormData(), { responseType: 'blob' }),
        axios.post('/api/sheet-preview-original', makeFormData(), { responseType: 'blob' }),
      ]);

      safeSetObjectUrl(setSheetPreview, cutRes.data);
      safeSetObjectUrl(setSheetWhitePreview, whiteRes.data);
      safeSetObjectUrl(setSheetOriginalPreview, origRes.data);
    } catch (e) {
      console.error('조판 미리보기(3종) 오류:', e);
    }
  }, [
    selectedFile, useSheet,
    mode, offsetMm, totalSizeMm, holeDiameterMm, standHeightMm, tabScale, holeX, holeY,
    sheetWmm, sheetHmm, safeAbsMm, safePct, qty
  ]);

    // 파일 선택
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    // 원본 미리보기는 프론트에서 즉시 생성
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });

    // 서버 미리보기 초기화
    setPreviewWithCutline((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setPreviewWhite((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setSheetPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setSheetWhitePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setSheetOriginalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  // 파일 선택 시: 크기 + 미리보기들 생성
  useEffect(() => {
    if (!selectedFile) return;

    calculateSize();

    const timer = setTimeout(() => {
      updatePreview();        // 칼선 포함
      updateWhitePreview();   // 화이트(1회)
      if (useSheet) updateSheetPreview(); // 조판(옵션일 때만)
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedFile, calculateSize, updatePreview, updateWhitePreview, useSheet, updateSheetPreview]);

  // 설정 변경 시: 칼선 포함 미리보기만 갱신 (화이트는 불필요)
  useEffect(() => {
    if (!selectedFile) return;

    const timer = setTimeout(() => {
      updatePreview();
    }, 500);

    return () => clearTimeout(timer);
  }, [mode, offsetMm, totalSizeMm, holeDiameterMm, standHeightMm, tabScale, holeX, holeY, selectedFile, updatePreview]);

  // ✅ 조판 옵션 변경 시: 조판 미리보기 갱신
  useEffect(() => {
    if (!selectedFile || !useSheet) return;

    const t = setTimeout(() => {
      updateSheetPreview();
    }, 500);

    return () => clearTimeout(t);
  }, [useSheet, sheetWmm, sheetHmm, safeAbsMm, safePct, qty, selectedFile, updateSheetPreview]);

  // 변환 요청
  const handleConvert = async () => {
    if (!selectedFile) {
      setMessage('이미지를 먼저 선택해주세요.');
      return;
    }

    setLoading(true);
    setMessage('변환 중...');

    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('mode', mode);
    formData.append('offset_mm', offsetMm);
    formData.append('total_size_mm', totalSizeMm);
    formData.append('hole_diameter_mm', holeDiameterMm);
    formData.append('stand_height_mm', standHeightMm);
    formData.append('tab_scale', tabScale);
    formData.append('split_files', splitFiles);
    formData.append('format', format);

    if (holeX) formData.append('hole_x', holeX);
    if (holeY) formData.append('hole_y', holeY);

    // ✅ 조판 옵션
    formData.append('use_sheet', useSheet);
    if (useSheet) {
      formData.append('sheet_w_mm', sheetWmm);
      formData.append('sheet_h_mm', sheetHmm);
      formData.append('safe_abs_mm', safeAbsMm);
      formData.append('safe_pct', safePct);
      formData.append('qty', qty);
    }

    try {
      const response = await axios.post('/api/convert', formData, {
        responseType: 'blob', // 중요: 바이너리 데이터 수신
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // -------------------------------------------------------------
      // [수정됨] 서버가 보낸 헤더나 데이터 타입으로 파일명/확장자 결정
      // -------------------------------------------------------------
      let filename = `knife_line.${format}`; // 기본값

      // 1. Content-Disposition 헤더 확인 (백엔드가 지정한 파일명 우선)
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      } else {
        // 2. 헤더가 없는 경우(CORS 등), 데이터 타입이나 옵션으로 추론
        const isZipType = response.data.type.includes('zip');
        const shouldBeZip = useSheet || (splitFiles && mode === 'stand');

        if (isZipType || shouldBeZip) {
          filename = 'knife_line_outputs.zip';
        }
      }

      // 다운로드 링크 생성
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // 결정된 파일명 사용
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage('변환 완료! 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('변환 오류:', error);
      setMessage('변환 중 오류가 발생했습니다: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ✅ 언마운트 시 URL 정리
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
      if (previewWithCutline) URL.revokeObjectURL(previewWithCutline);
      if (previewWhite) URL.revokeObjectURL(previewWhite);
      if (sheetPreview) URL.revokeObjectURL(sheetPreview);
      if (sheetWhitePreview) URL.revokeObjectURL(sheetWhitePreview);
      if (sheetOriginalPreview) URL.revokeObjectURL(sheetOriginalPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="App">
      <header className="App-header">
        <h1>🔪 칼선 생성기 (Knife Line Converter)</h1>
        <p>이미지를 업로드하고 SVG/AI 칼선 파일을 다운로드하세요</p>
      </header>

      <main className="container">
        {/* 파일 업로드 */}
        <div className="section">
          <h2>1. 이미지 업로드</h2>
          <input type="file" accept="image/png" onChange={handleFileChange} />
        </div>

        {/* 이미지 미리보기 */}
        {preview && (
          <div className="section">
            <h2>2. 실시간 미리보기</h2>

            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* 원본 */}
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>원본</h3>
                <div className="preview-checker">
                  <img
                    src={preview}
                    alt="Original"
                    style={{ maxWidth: '300px', border: '2px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>

              {/* 칼선 포함 미리보기 */}
              {previewWithCutline && (
                <div>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>칼선 포함</h3>
                  <div className="preview-checker">
                    <img
                      src={previewWithCutline}
                      alt="With Cutline"
                      style={{ maxWidth: '300px', border: '2px solid #f00', borderRadius: '4px' }}
                    />
                  </div>
                </div>
              )}

              {/* 화이트 미리보기 */}
              {previewWhite && (
                <div>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>화이트</h3>
                  <div className="preview-bg">
                    <img
                      src={previewWhite}
                      alt="White Preview"
                      style={{ maxWidth: '300px', border: '2px solid #999', borderRadius: '4px' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 설정 */}
        <div className="section">
          <h2>3. 설정</h2>

          <label>
            모드:
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="basic">기본 칼선</option>
              <option value="keyring">아크릴 키링</option>
              <option value="keyring_enhanced">고도화 키링</option>
              <option value="stand">스탠드형</option>
            </select>
          </label>

          <label>
            칼선 두께 (mm):
            <input
              type="number"
              value={offsetMm}
              onChange={(e) => setOffsetMm(parseFloat(e.target.value))}
              step="0.1"
              min="0"
            />
          </label>

          <label>
            전체 크기 - 가로+세로 합 (mm):
            <input
              type="number"
              value={totalSizeMm}
              onChange={(e) => setTotalSizeMm(parseFloat(e.target.value))}
              step="1"
              min="10"
            />
          </label>

          {(mode === 'keyring' || mode === 'keyring_enhanced') && (
            <>
              <label>
                구멍 직경 (mm):
                <input
                  type="number"
                  value={holeDiameterMm}
                  onChange={(e) => setHoleDiameterMm(parseFloat(e.target.value))}
                  step="0.1"
                  min="1"
                />
              </label>

              {mode === 'keyring_enhanced' && (
                <>
                  <label>
                    구멍 X 위치 (픽셀, 선택사항):
                    <input
                      type="number"
                      value={holeX}
                      onChange={(e) => setHoleX(e.target.value)}
                      placeholder="자동 배치"
                    />
                  </label>

                  <label>
                    구멍 Y 위치 (픽셀, 선택사항):
                    <input
                      type="number"
                      value={holeY}
                      onChange={(e) => setHoleY(e.target.value)}
                      placeholder="자동 배치"
                    />
                  </label>

                  <label>
                    고리 크기 배수:
                    <input
                      type="range"
                      value={tabScale}
                      onChange={(e) => setTabScale(parseFloat(e.target.value))}
                      step="0.1"
                      min="0.5"
                      max="2.0"
                      style={{ width: '200px' }}
                    />
                    <span style={{ marginLeft: '10px' }}>{tabScale.toFixed(1)}x</span>
                  </label>
                </>
              )}
            </>
          )}

          {mode === 'stand' && (
            <>
              <label>
                받침대 높이 (mm):
                <input
                  type="number"
                  value={standHeightMm}
                  onChange={(e) => setStandHeightMm(parseFloat(e.target.value))}
                  step="1"
                  min="10"
                  max="200"
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={splitFiles}
                  onChange={(e) => setSplitFiles(e.target.checked)}
                />
                앞면과 받침대를 분리 파일로 다운로드
              </label>
            </>
          )}

          <label>
            출력 형식:
            <select value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="svg">SVG</option>
              <option value="ai">AI (Illustrator)</option>
              <option value="dxf">DXF</option>
            </select>
          </label>

          {/* 계산된 크기 표시 */}
          {selectedFile && sizeInfo && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              border: '1px solid #2196F3'
            }}>
              <strong>📏 예상 크기:</strong>
              <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>가로:</strong> {sizeInfo.width_cm} cm ({sizeInfo.width_mm} mm)
                  </p>
                </div>
                <div>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>세로:</strong> {sizeInfo.height_cm} cm ({sizeInfo.height_mm} mm)
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '12px', color: '#666', margin: '10px 0 0 0' }}>
                * 가로+세로 합: {totalSizeMm}mm (원본 비율 유지)
              </p>
            </div>
          )}
        </div>

        {/* ✅ 조판 옵션 */}
        <div className="section">
          <h2>4. 조판 옵션 (선택사항)</h2>

          <label className="checkline">
            <input
              type="checkbox"
              checked={useSheet}
              onChange={(e) => {
                const next = e.target.checked;
                setUseSheet(next);

                // 꺼질 때 미리보기 제거
                if (!next) {
                  setSheetPreview((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return null;
                  });
                }
              }}
            />
            아크릴 조판 최적화 사용
          </label>

          {useSheet && (
            <>
              <h3 style={{ marginTop: '10px' }}>시트 크기</h3>

              <label>
                너비 (mm):
                <input
                  type="number"
                  value={sheetWmm}
                  onChange={(e) => setSheetWmm(parseFloat(e.target.value))}
                  min="50"
                  step="1"
                />
              </label>

              <label>
                높이 (mm):
                <input
                  type="number"
                  value={sheetHmm}
                  onChange={(e) => setSheetHmm(parseFloat(e.target.value))}
                  min="50"
                  step="1"
                />
              </label>

              <div className="preset-row">
                <button type="button" className="preset-btn" onClick={() => { setSheetWmm(900); setSheetHmm(600); }}>
                  600×900mm
                </button>
                <button type="button" className="preset-btn" onClick={() => { setSheetWmm(600); setSheetHmm(450); }}>
                  450×600mm
                </button>
                <button type="button" className="preset-btn" onClick={() => { setSheetWmm(400); setSheetHmm(300); }}>
                  300×400mm
                </button>
              </div>

              <h3 style={{ marginTop: '20px' }}>안전마진</h3>

              <label>
                절대값 (mm):
                <input
                  type="number"
                  value={safeAbsMm}
                  onChange={(e) => setSafeAbsMm(parseFloat(e.target.value))}
                  min="0"
                  step="0.1"
                />
              </label>

              <label>
                퍼센트 (%):
                <input
                  type="number"
                  value={safePct}
                  onChange={(e) => setSafePct(parseFloat(e.target.value))}
                  min="0"
                  step="1"
                />
              </label>

              <label>
                배치 개수:
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(parseInt(e.target.value, 10) || 0)}
                  min="1"
                  step="1"
                />
              </label>

              <p style={{ fontSize: '12px', color: '#666' }}>
                더 큰 값이 자동으로 적용됩니다. (기계 오차 고려)
              </p>
            </>
          )}
        </div>

        {/* ✅ 조판 미리보기 (칼선 + 화이트 + 원본) */}
        {useSheet && (sheetPreview || sheetWhitePreview || sheetOriginalPreview) && (
          <div className="section">
            <h2>조판 미리보기</h2>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
              {/* 칼선 조판 */}
              {sheetPreview && (
                <div style={{ flex: '1 1 420px', minWidth: 320 }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>칼선 조판</h3>
                  <div className="sheet-preview-frame">
                    <img
                      src={sheetPreview}
                      alt="Sheet Preview (Cutline)"
                      className="sheet-preview-img"
                    />
                  </div>
                </div>
              )}

              {/* 화이트 조판 */}
              {sheetWhitePreview && (
                <div style={{ flex: '1 1 420px', minWidth: 320 }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>화이트 조판</h3>
                  <div className="sheet-preview-frame preview-checker">
                    <img
                      src={sheetWhitePreview}
                      alt="Sheet Preview (White)"
                      className="sheet-preview-img"
                    />
                  </div>
                </div>
              )}

              {/* ✅ 원본 조판 */}
              {sheetOriginalPreview && (
                <div style={{ flex: '1 1 420px', minWidth: 320 }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>원본 조판</h3>
                  <div className="sheet-preview-frame preview-checker">
                    <img
                      src={sheetOriginalPreview}
                      alt="Sheet Preview (Original)"
                      className="sheet-preview-img"
                    />
                  </div>
                </div>
              )}
            </div>

            <p style={{ color: '#2e7d32', marginTop: '10px' }}>
              ✓ 조판 미리보기 준비 완료
            </p>
          </div>
        )}


        {/* 변환 버튼 */}
        <div className="section">
          <button onClick={handleConvert} disabled={loading || !selectedFile}>
            {loading ? '변환 중...' : '변환 및 다운로드'}
          </button>
        </div>

        {/* 메시지 */}
        {message && (
          <div className="section">
            <p style={{
              color: message.includes('오류') ? 'red' : message.includes('완료') ? 'green' : '#666',
              fontWeight: message.includes('완료') ? 'bold' : 'normal'
            }}>
              {message}
            </p>
          </div>
        )}

        {/* 설문조사 */}
        <div className="section" style={{
          textAlign: 'center',
          marginTop: '40px',
          paddingTop: '30px',
          borderTop: '2px solid #eee'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>
            💬 사용 후기를 들려주세요!
          </h3>
          <p style={{
            marginBottom: '20px',
            color: '#666',
            fontSize: '14px'
          }}>
            더 나은 서비스를 만들기 위해 여러분의 의견이 필요합니다.
          </p>
          <a
            href="https://www.notion.so/2a9008ec38158069bb42ebffc550d442?pvs=106"
            target="_blank"
            rel="noopener noreferrer"
            className="survey-button"
          >
            📝 설문조사 참여하기
          </a>
          <p style={{
            marginTop: '15px',
            fontSize: '12px',
            color: '#999'
          }}>
            소요 시간: 약 3~5분
          </p>
        </div>
      </main>
    </div>
  );
}

export default App;
